import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import { dataPipelines } from '../../../../../../api/coreData'
import { postNamedQuery } from '../../../../../../service'
import { flattenNoFollow } from '../../../../../../utils/flatten'
import { decipherError } from '../../../../../../utils/decipherError'
import { useDenaliContext } from '../../../DenaliProvider'
import { SUCCESS_ALERT } from '../../../../../../constants'
import { validateTableDataRequest } from './validateTableDataRequest'
import { usePipelineForm } from './PipelineFormContext'
import stableStringify from './stableStringify'

const PipelineWorkspaceContext = createContext({})

export const usePipelineWorkspace = () => useContext(PipelineWorkspaceContext)

function PipelineWorkspaceProvider ({ initialValue, children }) {
  const { setAlert } = useDenaliContext()
  const queryClient = useQueryClient()
  const form = usePipelineForm()

  const editorRef = useRef(null)
  const [currentQuery, setCurrentQuery] = useState(null)
  const [variables, setVariables] = useState(initialValue.pipeline?.variables || {})
  const [parsedRequest, setParsedRequest] = useState(null)
  const [analysis, setAnalysis] = useState(initialValue.analysis || { fields: [] })
  const [requestValidation, setRequestValidation] = useState(null)
  const [resultMode, setResultMode] = useState('table')

  const [pipelineJson, setPipelineJson] = useState(() => {
    try {
      return JSON.stringify(initialValue.pipeline?.components || [], null, 2)
    } catch {
      return '[]'
    }
  })

  const [proposedPipelineJson, setProposedPipelineJson] = useState(null)
  const [diffMode, setDiffMode] = useState(false)

  const savedWorkspaceRef = useRef(stableStringify({
    pipelineJson: (() => { try { return JSON.stringify(initialValue.pipeline?.components || [], null, 2) } catch { return '[]' } })(),
    variables: initialValue.pipeline?.variables || {}
  }))

  const { data: response, isFetching: responseFetching, error: responseError, refetch } = dataPipelines.useExecuteDataPipeline(parsedRequest, { enabled: !!parsedRequest, bypass: true })
  const { mutateAsync: createDataPipeline } = dataPipelines.useCreateDataPipeline()
  const { mutateAsync: modifyDataPipeline } = dataPipelines.useModifyDataPipeline()

  useEffect(() => {
    if (responseError || !response?.data?.length) return
    const data = response.data
    const allColumns = data.reduce((p, c) => {
      const cFlat = flattenNoFollow(c)
      const fields = Object.entries(cFlat)
      fields.forEach(([key, value]) => {
        if (key in p && p[key] !== 'null') return
        if (value === null) {
          p[key] = 'null'
          return
        }
        p[key] = typeof value
      })
      return p
    }, {})
    const fields = Object.entries(allColumns).map(([key, type]) => ({
      accessor: key,
      type
    }))
    setAnalysis({ fields })
  }, [response, responseError])

  const handleEditorChange = useCallback((newJson) => {
    setPipelineJson(newJson)
  }, [])

  const handleRun = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const valid = validateTableDataRequest(pipelineJson, variables)
    setRequestValidation(valid)
    if (valid.isValid) {
      setCurrentQuery(valid.request)
      setParsedRequest(valid.parsedRequest)
    }
  }, [pipelineJson, variables])

  const handleSave = useCallback(async () => {
    const validationResult = form.validate()
    if (validationResult) return

    let components = currentQuery
    if (!components) {
      try {
        components = JSON.parse(pipelineJson)
      } catch {
        return
      }
    }

    const command = {
      displayName: form.displayName,
      code: form.code,
      description: form.description,
      scope: form.scope,
      pipeline: {
        components,
        variables
      },
      analysis,
      pipelineVersion: 2
    }

    if (form.pipelineId) {
      command.pipelineId = form.pipelineId
    }

    try {
      form.setProcessing(true)
      form.setSubmitError(null)
      const response = form.pipelineId ? await modifyDataPipeline(command) : await createDataPipeline(command)
      if (response?.statusCode === 500) {
        throw new Error('There was a problem saving the data pipeline')
      }

      form.markFormClean()
      savedWorkspaceRef.current = stableStringify({ pipelineJson, variables })

      setAlert({
        ...SUCCESS_ALERT,
        alertMessage: `Data Pipeline "${form.displayName}" saved!`
      })

      if (!form.pipelineId && response.pipelineId) {
        form.setPipelineId(response.pipelineId)
        // Use replaceState instead of React Router's history.push/replace to update
        // the URL without triggering a route change. A route change would remount
        // EditPipelineHome and destroy in-memory state (chat conversation, editor
        // contents, etc.). The trade-off is that useParams() / usePipelineId() in
        // index.jsx remains stale — form.pipelineId is the source of truth for all
        // save and reset operations within the provider tree.
        window.history.replaceState(null, '', `/admin/denali/analysis/pipelines/${response.pipelineId}`)
      }

      return response
    } catch (error) {
      form.setSubmitError(decipherError(error))
    } finally {
      form.setProcessing(false)
    }
  }, [form, createDataPipeline, modifyDataPipeline, analysis, variables, currentQuery, pipelineJson, setAlert])

  const setProposedPipeline = useCallback((pipelineJsonString) => {
    try {
      const parsed = JSON.parse(pipelineJsonString)
      if (Array.isArray(parsed)) {
        setProposedPipelineJson(JSON.stringify(parsed, null, 2))
        setDiffMode(true)
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to parse proposed pipeline JSON:', error)
      return false
    }
  }, [])

  const acceptProposal = useCallback(() => {
    if (!proposedPipelineJson) return
    try {
      const parsed = JSON.parse(proposedPipelineJson)
      if (Array.isArray(parsed)) {
        const formatted = JSON.stringify(parsed, null, 2)
        setPipelineJson(formatted)
        setCurrentQuery(parsed)
        setProposedPipelineJson(null)
        setDiffMode(false)
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to accept proposal:', error)
      return false
    }
  }, [proposedPipelineJson])

  const rejectProposal = useCallback(() => {
    setProposedPipelineJson(null)
    setDiffMode(false)
  }, [])

  const isWorkspaceDirty = useMemo(() => {
    const current = stableStringify({ pipelineJson, variables })
    return current !== savedWorkspaceRef.current
  }, [pipelineJson, variables])

  const resetPipeline = useCallback(async () => {
    if (!form.pipelineId) return
    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['core/get-data-pipeline', form.pipelineId],
        queryFn: async () => {
          const response = await postNamedQuery('coreData', 'get-data-pipeline', {
            pipelineId: form.pipelineId
          })
          return response.data
        },
        staleTime: 0
      })
      if (result) {
        const pipelineData = result.pipeline || result
        const components = pipelineData.components || pipelineData.pipeline?.components
        if (!components || !Array.isArray(components)) {
          console.error('Invalid pipeline components in reset response. Result:', result)
          return
        }
        const vars = pipelineData.variables || pipelineData.pipeline?.variables || {}

        form.resetFormFields({
          displayName: result.displayName || pipelineData.displayName || '',
          code: result.code || pipelineData.code || '',
          description: result.description || pipelineData.description || '',
          scope: result.scope || pipelineData.scope || 'client'
        })

        setVariables(vars)
        const json = JSON.stringify(components, null, 2)
        setPipelineJson(json)
        setCurrentQuery(components)
        setAnalysis(result.analysis || pipelineData.analysis || { fields: [] })

        savedWorkspaceRef.current = stableStringify({ pipelineJson: json, variables: vars })
      }
    } catch (error) {
      console.error('Failed to reset pipeline:', error)
    }
  }, [form, queryClient])

  const value = useMemo(() => ({
    editorRef,
    pipelineJson,
    handleEditorChange,
    variables,
    setVariables,
    currentQuery,
    analysis,
    requestValidation,
    resultMode,
    setResultMode,
    response,
    responseFetching,
    responseError,
    refetch,
    handleRun,
    handleSave,
    resetPipeline,
    diffMode,
    proposedPipelineJson,
    setProposedPipeline,
    acceptProposal,
    rejectProposal,
    isWorkspaceDirty
  }), [
    pipelineJson, handleEditorChange,
    variables,
    currentQuery, analysis, requestValidation,
    resultMode,
    response, responseFetching, responseError, refetch,
    handleRun, handleSave, resetPipeline,
    diffMode, proposedPipelineJson, setProposedPipeline, acceptProposal, rejectProposal,
    isWorkspaceDirty
  ])

  return (
    <PipelineWorkspaceContext.Provider value={value}>
      {children}
    </PipelineWorkspaceContext.Provider>
  )
}

PipelineWorkspaceProvider.propTypes = {
  initialValue: PropTypes.object.isRequired,
  children: PropTypes.node
}

export default PipelineWorkspaceProvider
